import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, X, type LucideIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * How a chip reads: plainly, as one of several alternatives, as something
 * every match must carry, or as something ruled out.
 */
export type ChipTone = 'neutral' | 'include' | 'required' | 'exclude'

export interface Chip {
  label: string
  tone?: ChipTone
}

/** The badge each tone is drawn as — shared with anything else showing values. */
export const CHIP_VARIANTS: Record<ChipTone, 'gray' | 'success' | 'blue' | 'red'> = {
  neutral: 'gray',
  include: 'success',
  required: 'blue',
  exclude: 'red',
}

export interface ChipRowProps {
  /** Label down the left, e.g. "Locations". */
  label: string
  /** Shown before the label. */
  icon?: LucideIcon
  chips: Chip[]
  /**
   * How many chips to show before the rest are counted into a "+n more". Clicking it, or the
   * "… more" shown when a long chip is cut short, opens the row up to every chip in full.
   */
  max?: number
  /** Rendered after the chips, e.g. a badge saying where the values came from. */
  children?: React.ReactNode
  /** Puts the label above the chips instead of beside them — for narrow columns. */
  stacked?: boolean
  className?: string
}

/** One labelled row of chips — a compact way to summarise a list of values. */
export function ChipRow({
  label,
  icon: Icon,
  chips,
  max = 6,
  children,
  stacked,
  className,
}: ChipRowProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const chipsRef = useRef<HTMLDivElement>(null)
  // Indices of the chips whose label runs past the row and fades out instead.
  const [clipped, setClipped] = useState<ReadonlySet<number>>(new Set())
  const shown = expanded ? chips : chips.slice(0, max)
  const hidden = chips.length - shown.length

  useLayoutEffect(() => {
    const container = chipsRef.current
    if (!container || expanded) {
      setClipped((prev) => (prev.size ? new Set() : prev))
      return
    }
    const measure = () => {
      const labels = container.querySelectorAll<HTMLElement>('[data-chip-label]')
      const next = new Set<number>()
      labels.forEach((label, index) => {
        if (label.scrollWidth > label.clientWidth) next.add(index)
      })
      setClipped((prev) =>
        prev.size === next.size && [...next].every((index) => prev.has(index)) ? prev : next
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [chips, expanded])

  const expandable = hidden > 0 || clipped.size > 0

  return (
    <div
      className={cn('flex flex-col gap-1.5 py-2.5', !stacked && 'sm:flex-row sm:gap-4', className)}
    >
      <span
        className={cn(
          'flex shrink-0 items-center gap-2 pt-0.5 text-sm font-medium',
          !stacked && 'sm:w-40'
        )}
      >
        {Icon && <Icon className="size-4 shrink-0 text-muted-foreground" />}
        {label}
      </span>
      <div ref={chipsRef} className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {shown.map((chip, index) => {
          const tone = chip.tone ?? 'neutral'
          return (
            <Badge
              key={`${chip.label}-${index}`}
              variant={CHIP_VARIANTS[tone]}
              className={cn(
                'max-w-full font-normal',
                expanded && 'h-auto justify-start rounded-lg text-left whitespace-normal'
              )}
              title={clipped.has(index) ? chip.label : undefined}
            >
              {tone === 'include' && <Check className="size-3" />}
              {tone === 'exclude' && <X className="size-3" />}
              <span
                data-chip-label
                className={cn(
                  'min-w-0',
                  !expanded && 'overflow-hidden',
                  clipped.has(index) && 'mask-r-from-[calc(100%-2rem)]'
                )}
              >
                {chip.label}
              </span>
            </Badge>
          )
        })}
        {(expandable || expanded) && (
          <Badge variant="gray" className="font-normal hover:bg-(--ad-badge-gray-border)" asChild>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded
                ? t('common.showLess')
                : hidden > 0
                  ? t('common.andMoreCount', { count: hidden })
                  : t('common.showMoreEllipsis')}
            </button>
          </Badge>
        )}
        {children}
      </div>
    </div>
  )
}
